import { MSG_API_BASE_URI } from 'Constants/env';
import { useNotificationStore } from 'Modules/notification/store';
import { useCallback, useRef } from 'react';
import {
  ANALYTICS_EVENT_FILE_UPLOAD_ERROR_DISPLAYED,
  ANALYTICS_EVENT_FILE_UPLOAD_SUCCESS,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { resizeImage } from 'Utils/resizeImage';
import API, { PURE_API } from '~/api';
import { useAttachmentStore } from '../../store/attachments';
import {
  AttachmentItem,
  S3Object,
  S3PostFormData,
} from '../../store/attachments/index.types';

interface UseFileUploadProps {
  uploadAll: (attachments: AttachmentItem[]) => Promise<S3Object[]>;
  cancelUpload: (attachmentId: AttachmentItem['id']) => void;
}

const createS3Object = async (
  fileHandle: {
    filename: string;
    contentType: string;
    generatePreviewLink: boolean;
    previewContentType?: string;
  },
  signal: AbortSignal
) => {
  return API.post<S3Object>(
    `${MSG_API_BASE_URI}fileHandle?method=POST`,
    fileHandle,
    {
      signal,
    }
  );
};

/**
 * Builds a multipart form payload for an S3 presigned POST upload.
 * The signed fields returned by the backend must be appended before the
 * `file` field; otherwise S3 rejects the request with an invalid-signature
 * error.
 */
const buildS3PostFormData = (
  file: File,
  fields: S3PostFormData | undefined
): FormData => {
  const formData = new FormData();

  if (fields) {
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  formData.append('file', file);

  return formData;
};

const postContentToS3 = async (
  file: File,
  uploadUrl: string,
  fields: S3PostFormData | undefined,
  signal: AbortSignal
) => {
  const formData = buildS3PostFormData(file, fields);

  return PURE_API.post(uploadUrl, formData, {
    signal,
    headers: {
      'Content-Type': undefined,
    },
  });
};

/**
 * Uploads the generated preview image. The backend currently returns a
 * presigned PUT URL (auth in the query string), so we keep a direct PUT with
 * the raw file body here even though the primary upload moved to POST.
 */
async function uploadImageFile(
  file: File,
  s3Object: S3Object,
  signal: AbortSignal
): Promise<void> {
  if (!s3Object.previewUrl) {
    return;
  }

  const extension = file.type.split('/')[1];
  const shouldResize = !file.type.includes('svg');
  const fileToUpload = shouldResize ? await resizeImage(file, extension) : file;

  await PURE_API.put(s3Object.previewUrl, fileToUpload, {
    headers: {
      'Content-Type': fileToUpload.type || file.type,
    },
    signal,
  });
}

const useFileUpload = (): UseFileUploadProps => {
  const abortControllers = useRef(
    new Map<AttachmentItem['id'], AbortController>()
  );

  const { updateStatus, setS3Object } = useAttachmentStore();

  const uploadToAws = async (
    attachment: AttachmentItem,
    signal: AbortSignal
  ) => {
    const fileHandle = {
      filename: attachment?.file.name,
      contentType: attachment?.file.type,
      generatePreviewLink: true,
      previewContentType: attachment?.file.type,
    };
    const { data: s3Object } = await createS3Object(fileHandle, signal);
    if (!s3Object) {
      throw new Error('Error creating s3Object file handle');
    }

    const response = await postContentToS3(
      attachment.file,
      s3Object.url,
      s3Object.formData,
      signal
    );

    if (response.status < 400 && attachment.isImage) {
      await uploadImageFile(attachment.file, s3Object, signal);
    }

    return s3Object;
  };

  const uploadFile = async (attachment: AttachmentItem) => {
    const controller = new AbortController();
    abortControllers.current.set(attachment.id, controller);

    try {
      updateStatus(attachment.id, 'uploading');

      const s3Object = await uploadToAws(attachment, controller.signal);

      updateStatus(attachment.id, 'success');
      setS3Object(attachment.id, s3Object);

      pushToGTMDataLayer(ANALYTICS_EVENT_FILE_UPLOAD_SUCCESS, {
        file_size: attachment.file.size,
        file_type: attachment.file.type,
      });

      return s3Object;
    } catch (error) {
      if (controller.signal.aborted) {
        updateStatus(attachment.id, 'canceled');
      } else {
        updateStatus(attachment.id, 'error', error.message);
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          autoDismiss: true,
          title: 'Error uploading the file',
          message: 'We couldn’t upload the file. Please try again.',
        });
        pushToGTMDataLayer(ANALYTICS_EVENT_FILE_UPLOAD_ERROR_DISPLAYED, {
          reason: 'upload_failure',
        });
      }
    } finally {
      abortControllers.current.delete(attachment.id);
    }
  };

  const uploadAll = async (
    attachments: AttachmentItem[]
  ): Promise<S3Object[]> => {
    const pendingAttachments = attachments.filter(
      (attachment) => attachment.status === 'pending'
    );
    if (pendingAttachments.length === 0) {
      return;
    }

    return Promise.all(
      pendingAttachments.map((attachment) => uploadFile(attachment))
    );
  };
  const cancelUpload = useCallback((attachmentId: AttachmentItem['id']) => {
    const controller = abortControllers.current.get(attachmentId);
    if (controller) {
      controller.abort();
    }
  }, []);

  return {
    uploadAll,
    cancelUpload,
  };
};

export default useFileUpload;
